import React, { useState } from 'react';
import { Form, Input, Checkbox, Select, Switch, Card, Row, Col, Typography, Alert, Space, Divider } from 'antd';
import { CalendarOutlined, InfoCircleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;
const { Option } = Select;

const dayNames = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const dayShortNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const monthNames = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

interface CreateArrivalDeptRuleProps {
  formProps: any;
}

export const CreateArrivalDeptRule: React.FC<CreateArrivalDeptRuleProps> = ({ formProps }) => {
  const [isGlobal, setIsGlobal] = useState(false);
  const [arrivalDays, setArrivalDays] = useState<number[]>([]);
  const [departureDays, setDepartureDays] = useState<number[]>([]);

  // Return early if formProps is not available yet
  if (!formProps) {
    return <div>Loading...</div>;
  }

  const handleIsGlobalChange = (checked: boolean) => {
    setIsGlobal(checked);
    if (checked) {
      formProps.form?.setFieldsValue({
        valid_from_month: 1,
        valid_to_month: 12,
        recurring: true,
      });
    }
  };

  const renderDaySelector = (fieldName: string, title: string, color: string, selectedDays: number[], setSelectedDays: (days: number[]) => void) => (
    <Card size="small" title={title} style={{ marginBottom: 16 }}>
      <Form.Item
        name={fieldName}
        rules={[
          { required: true, message: `Bitte wählen Sie mindestens einen ${title.toLowerCase()}tag aus` }
        ]}
      >
        <Checkbox.Group
          value={selectedDays}
          onChange={(checkedValues) => {
            const numericValues = checkedValues.map(v => Number(v));
            setSelectedDays(numericValues);
            formProps.form?.setFieldsValue({ [fieldName]: numericValues });
          }}
          style={{ width: '100%' }}
        >
          <Row gutter={[8, 8]}>
            {dayNames.map((dayName, index) => (
              <Col span={24} key={index}>
                <Checkbox 
                  value={index}
                  style={{ 
                    width: '100%',
                    padding: '8px 12px',
                    border: selectedDays.includes(index) ? `2px solid ${color}` : '1px solid #d9d9d9',
                    borderRadius: '6px',
                    backgroundColor: selectedDays.includes(index) ? `${color}10` : 'transparent',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Space>
                    <Text strong>{dayShortNames[index]}</Text>
                    <Text>{dayName}</Text>
                  </Space>
                </Checkbox>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>
      </Form.Item>
    </Card>
  );

  const getPreview = () => {
    if (arrivalDays.length === 0 || departureDays.length === 0) return null;

    const arrivalText = arrivalDays.map(day => dayShortNames[day]).join(', ');
    const departureText = departureDays.map(day => dayShortNames[day]).join(', ');
    const validFromMonth = formProps.form?.getFieldValue('valid_from_month') || 1;
    const validToMonth = formProps.form?.getFieldValue('valid_to_month') || 12;
    const recurring = formProps.form?.getFieldValue('recurring');

    let validityText = '';
    if (isGlobal) {
      validityText = 'ganzjährig gültig';
    } else {
      validityText = `gültig von ${monthNames[validFromMonth - 1]} bis ${monthNames[validToMonth - 1]}`;
      if (recurring) {
        validityText += ', jährlich wiederkehrend';
      }
    }

    return (
      <Alert
        message="Regelvorschau"
        description={`Anreise: ${arrivalText} • Abreise: ${departureText} • ${validityText}`}
        type="info"
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 16 }}
      />
    );
  };

  return (
    <Form
      {...formProps}
      layout="vertical"
      initialValues={{
        arrival_days: [],
        departure_days: [],
        valid_from_month: 1,
        valid_to_month: 12,
        recurring: true,
        is_global: false,
      }}
    >
      {/* Label */}
      <Form.Item
        label="Regelbezeichnung"
        name="label"
        rules={[
          { required: true, message: 'Bitte geben Sie eine Bezeichnung ein' },
          { min: 3, message: 'Die Bezeichnung muss mindestens 3 Zeichen lang sein' }
        ]}
      >
        <Input 
          placeholder="z.B. 'Sommerferien-Regel' oder 'Standard An-/Abreise'"
          size="large"
          prefix={<CalendarOutlined />}
        />
      </Form.Item>

      {getPreview()}

      <Row gutter={16}>
        <Col span={12}>
          {renderDaySelector('arrival_days', 'Anreisetage', '#52c41a', arrivalDays, setArrivalDays)}
        </Col>
        <Col span={12}>
          {renderDaySelector('departure_days', 'Abreisetage', '#1890ff', departureDays, setDepartureDays)}
        </Col>
      </Row>

      <Divider>Gültigkeitszeitraum</Divider>

      {/* Ganzjährig gültig */}
      <Form.Item
        label="Ganzjährig gültig"
        name="is_global"
        valuePropName="checked"
      >
        <Switch
          checkedChildren={<CheckOutlined />}
          unCheckedChildren={<CloseOutlined />}
          onChange={handleIsGlobalChange}
        />
      </Form.Item>

      {!isGlobal && (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Gültig von Monat"
                name="valid_from_month"
                rules={[{ required: true, message: 'Bitte wählen Sie einen Startmonat' }]}
              >
                <Select placeholder="Startmonat wählen">
                  {monthNames.map((month, index) => (
                    <Option key={index + 1} value={index + 1}>
                      {month}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Gültig bis Monat"
                name="valid_to_month"
                rules={[{ required: true, message: 'Bitte wählen Sie einen Endmonat' }]}
              >
                <Select placeholder="Endmonat wählen">
                  {monthNames.map((month, index) => (
                    <Option key={index + 1} value={index + 1}>
                      {month}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Jährlich wiederholen"
            name="recurring"
            valuePropName="checked"
          >
            <Switch
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </Form.Item>
        </>
      )}

      <Alert
        message="Hinweis"
        description="Nach dem Erstellen können Sie diese Regel Ihren Objekten zuweisen. Gäste können dann nur an den erlaubten Wochentagen an- bzw. abreisen."
        type="info"
        showIcon
        style={{ marginTop: 16 }}
      />
    </Form>
  );
}; 